/**
 * HRMS Data Import — Admin Module
 *
 * Provides site-specific CSV import for employee master and work hours data.
 * Features:
 * - File upload with drag-and-drop
 * - Site selector for data scoping
 * - Processing audit log with status tracking
 * - Manual inbox sync trigger
 * - Workforce snapshot computation trigger
 *
 * SOC 2 Controls:
 * - CC6.1: Site-scoped data import
 * - CC7.2: All imports tracked via audit log
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Upload,
  FileSpreadsheet,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Copy,
  Users,
  Timer,
  FolderSync,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';

const SAMPLE_CSV = {
  employee_master: {
    filename: 'employee_master_template.csv',
    content: `employee_code,name,employment_type,gender,department,designation,joining_date,status,category,disability_flag,is_primary_site,last_updated
EMP001,John Smith,permanent,male,Operations,Safety Officer,2024-01-15,active,general,false,true,2024-06-01
EMP002,Jane Doe,permanent,female,HSE,HSE Manager,2023-06-10,active,general,false,true,2024-06-01
CON001,Bob Wilson,contract,male,Maintenance,Technician,2024-03-01,active,general,false,true,2024-06-01`,
  },
  work_hours: {
    filename: 'work_hours_template.csv',
    content: `employee_code,work_date,hours_worked,overtime_hours,shift_type
EMP001,2024-03-01,8,1.5,day
EMP001,2024-03-02,8,0,day
EMP002,2024-03-01,9,2,day
CON001,2024-03-01,8,0,night`,
  },
};

const FILE_TYPES = [
  { value: 'employee_master', label: 'Employee Master', description: 'Employee demographics, employment type, department' },
  { value: 'work_hours', label: 'Work Hours', description: 'Daily work hours per employee' },
];

const STATUS_STYLES = {
  success: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
  failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle },
  duplicate: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Copy },
  processing: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: RefreshCw },
  pending: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', icon: Clock },
};

const HRMSDataImport = ({ user }) => {
  // Site context
  const { activeSiteId, activeSiteName, availableSites, isCorpUser } = useSite();
  const { showSuccess, showError: showToastError, showInfo } = useAlert();

  // Upload state
  const [selectedFileType, setSelectedFileType] = useState('employee_master');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [expandedLogId, setExpandedLogId] = useState(null);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Snapshot state
  const [computingSnapshot, setComputingSnapshot] = useState(false);
  const [snapshotYear, setSnapshotYear] = useState(new Date().getFullYear());
  const [snapshotMonth, setSnapshotMonth] = useState(new Date().getMonth() + 1);
  const [snapshotResult, setSnapshotResult] = useState(null);

  // Error/success
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize selected site from context
  useEffect(() => {
    if (activeSiteId && !selectedSiteId) {
      setSelectedSiteId(activeSiteId);
      setSelectedSiteName(activeSiteName || '');
    }
  }, [activeSiteId, activeSiteName, selectedSiteId]);

  // Fetch audit logs when site changes
  useEffect(() => {
    if (selectedSiteId) {
      fetchAuditLogs();
    }
  }, [selectedSiteId, auditPage]);

  // ========== API Calls ==========

  const fetchAuditLogs = useCallback(async () => {
    if (!selectedSiteId) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({
        site_id: selectedSiteId,
        limit: '10',
        offset: String(auditPage * 10),
      });
      const response = await axios.get(`${API}/hrms/audit-log?${params}`);
      setAuditLogs(response.data.data || []);
      setAuditTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [selectedSiteId, auditPage]);

  const handleUpload = async () => {
    if (!selectedFile || !selectedSiteId) {
      setError('Please select a site and a CSV file');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    const params = new URLSearchParams({
      site_id: selectedSiteId,
    });
    if (selectedSiteName) {
      params.append('site_name', selectedSiteName);
    }

    try {
      const response = await axios.post(
        `${API}/hrms/upload/${selectedFileType}?${params}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadResult(response.data.data);
      setSuccess('File processed successfully');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchAuditLogs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleTriggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      const response = await axios.post(`${API}/hrms/trigger-sync`);
      setSyncResult(response.data);
      setSuccess(`Inbox scan complete: ${response.data.files_processed} files processed`);
      fetchAuditLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleComputeSnapshot = async () => {
    if (!selectedSiteId) {
      showToastError('Please select a site');
      return;
    }

    setComputingSnapshot(true);
    setSnapshotResult(null);

    try {
      const params = new URLSearchParams({
        site_id: selectedSiteId,
        year: String(snapshotYear),
        month: String(snapshotMonth),
      });
      if (selectedSiteName) params.append('site_name', selectedSiteName);

      const response = await axios.post(`${API}/hrms/compute-snapshot?${params}`);
      setSnapshotResult(response.data.data);
      const snap = response.data.data;
      showSuccess(
        `Snapshot computed: ${snap.total_employees} employees, ${snap.total_contractors} contractors, ${snap.total_manhours?.toFixed(0) || 0} manhours`
      );
    } catch (err) {
      showToastError(err.response?.data?.detail || 'Snapshot computation failed');
    } finally {
      setComputingSnapshot(false);
    }
  };

  // ========== Drag & Drop ==========

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
      } else {
        setError('Only .csv files are accepted');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // ========== Helpers ==========

  const getSiteOptions = () => {
    if (availableSites && availableSites.length > 0) {
      return availableSites.map(s => ({
        id: s.site_id || s.site_node_id || s.id,
        name: s.site_name || s.name || s.site_id || 'Unknown',
      }));
    }
    return [];
  };

  const downloadTemplate = (fileType) => {
    const template = SAMPLE_CSV[fileType];
    if (!template) return;
    const blob = new Blob([template.content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = template.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showInfo(`Downloaded ${template.filename}`);
  };

  const clearMessages = () => {
    setTimeout(() => { setError(null); setSuccess(null); }, 5000);
  };

  useEffect(() => {
    if (error || success) clearMessages();
  }, [error, success]);

  // ========== Render ==========

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Site Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#1B5C94]" />
            HRMS Data Import
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Import employee master data and work hours from CSV files for ESG reporting and workforce metrics
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Site <span className="text-red-600">*</span>
            </label>
            <select
              value={selectedSiteId}
              onChange={(e) => {
                const site = getSiteOptions().find(s => s.id === e.target.value);
                setSelectedSiteId(e.target.value);
                setSelectedSiteName(site?.name || '');
                setAuditPage(0);
              }}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select a site --</option>
              {getSiteOptions().map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {selectedSiteId && (
        <>
          {/* File Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#1B5C94]" />
                Upload CSV File
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File Type Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">File Type</label>
                <div className="flex gap-3">
                  {FILE_TYPES.map(ft => (
                    <button
                      key={ft.value}
                      onClick={() => setSelectedFileType(ft.value)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        selectedFileType === ft.value
                          ? 'border-[#1B5C94] bg-blue-50 text-[#1B5C94]'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{ft.label}</div>
                      <div className="text-xs mt-0.5 opacity-75">{ft.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Drag & Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-[#1B5C94] bg-blue-50'
                    : selectedFile
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {selectedFile ? (
                  <div>
                    <FileSpreadsheet className="w-10 h-10 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-green-700">{selectedFile.name}</p>
                    <p className="text-xs text-green-600 mt-1">
                      {(selectedFile.size / 1024).toFixed(1)} KB — Click or drop to replace
                    </p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      Drag and drop a CSV file here, or <span className="text-[#1B5C94] font-semibold">click to browse</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Only .csv files are accepted</p>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="bg-[#1B5C94] hover:bg-[#144a78] text-white"
                >
                  {uploading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload & Process</>
                  )}
                </Button>

                {selectedFile && !uploading && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`rounded-lg border p-4 ${
                  uploadResult.status === 'success' ? 'bg-green-50 border-green-200' :
                  uploadResult.status === 'duplicate' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <h4 className="font-semibold text-sm mb-2">
                    {uploadResult.status === 'success' ? 'Import Successful' :
                     uploadResult.status === 'duplicate' ? 'Duplicate File' :
                     'Import Failed'}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Total Rows:</span>{' '}
                      <span className="font-semibold">{uploadResult.rows_total}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Inserted:</span>{' '}
                      <span className="font-semibold text-green-700">{uploadResult.rows_inserted}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Updated:</span>{' '}
                      <span className="font-semibold text-blue-700">{uploadResult.rows_updated}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Failed:</span>{' '}
                      <span className="font-semibold text-red-700">{uploadResult.rows_failed}</span>
                    </div>
                  </div>
                  {uploadResult.error_details?.validation_errors?.length > 0 && (
                    <div className="mt-3 text-xs">
                      <p className="font-semibold text-red-700 mb-1">Validation Errors (first 10):</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {uploadResult.error_details.validation_errors.slice(0, 10).map((e, i) => (
                          <div key={i} className="text-red-600">
                            Row {e.row}: {e.errors.join('; ')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions Row: Inbox Sync + Snapshot */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Inbox Sync */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FolderSync className="w-5 h-5 text-[#1B5C94]" />
                  Inbox Sync
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Scan the SFTP inbox folder for new CSV files and process them
                </p>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleTriggerSync}
                  disabled={syncing}
                  variant="outline"
                  className="w-full"
                >
                  {syncing ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
                  ) : (
                    <><FolderSync className="w-4 h-4 mr-2" /> Trigger Inbox Scan</>
                  )}
                </Button>
                {syncResult && (
                  <div className="mt-3 text-sm">
                    <p className="text-gray-600">
                      Files processed: <span className="font-semibold">{syncResult.files_processed}</span>
                    </p>
                    {syncResult.results?.map((r, i) => (
                      <div key={i} className={`mt-1 text-xs ${
                        r.status === 'success' ? 'text-green-600' :
                        r.status === 'duplicate' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {r.file_name}: {r.status}
                        {r.rows_inserted > 0 && ` (+${r.rows_inserted} new)`}
                        {r.rows_updated > 0 && ` (~${r.rows_updated} updated)`}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Workforce Snapshot */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-5 h-5 text-[#1B5C94]" />
                  Compute Workforce Snapshot
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Aggregate employee headcount and manhours for BRSR/LTIFR reporting
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Year</label>
                    <input
                      type="number"
                      value={snapshotYear}
                      onChange={(e) => setSnapshotYear(parseInt(e.target.value) || new Date().getFullYear())}
                      min={2020}
                      max={2100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Month</label>
                    <select
                      value={snapshotMonth}
                      onChange={(e) => setSnapshotMonth(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button
                  onClick={handleComputeSnapshot}
                  disabled={computingSnapshot}
                  variant="outline"
                  className="w-full"
                >
                  {computingSnapshot ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Computing...</>
                  ) : (
                    <><BarChart3 className="w-4 h-4 mr-2" /> Compute Snapshot</>
                  )}
                </Button>
                {snapshotResult && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-blue-800 mb-1">
                      Snapshot: {snapshotResult.period}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                      <div>Employees: <strong>{snapshotResult.total_employees}</strong></div>
                      <div>Contractors: <strong>{snapshotResult.total_contractors}</strong></div>
                      <div>Male: <strong>{snapshotResult.male_count}</strong></div>
                      <div>Female: <strong>{snapshotResult.female_count}</strong></div>
                      <div>Total Manhours: <strong>{snapshotResult.total_manhours?.toFixed(1)}</strong></div>
                      <div>LTIFR: <strong>{snapshotResult.ltifr != null ? snapshotResult.ltifr.toFixed(4) : 'N/A'}</strong></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Timer className="w-5 h-5 text-[#1B5C94]" />
                  Import History
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAuditLogs}
                  disabled={auditLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${auditLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  No import history for this site yet
                </p>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map(log => {
                    const style = STATUS_STYLES[log.status] || STATUS_STYLES.pending;
                    const StatusIcon = style.icon;
                    const isExpanded = expandedLogId === log.id;

                    return (
                      <div
                        key={log.id}
                        className={`border rounded-lg ${style.border} ${style.bg}`}
                      >
                        <div
                          className="flex items-center gap-3 p-3 cursor-pointer"
                          onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                        >
                          <StatusIcon className={`w-5 h-5 ${style.text} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm truncate">{log.file_name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${style.bg} ${style.text} border ${style.border}`}>
                                {log.status}
                              </span>
                            </div>
                            <div className="flex gap-4 text-xs text-gray-500 mt-1">
                              <span>{log.file_type}</span>
                              <span>{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                              {log.rows_total > 0 && (
                                <span>
                                  {log.rows_inserted} new, {log.rows_updated} updated
                                  {log.rows_failed > 0 && `, ${log.rows_failed} failed`}
                                </span>
                              )}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>

                        {isExpanded && (
                          <div className="px-3 pb-3 border-t border-gray-200 pt-2 text-xs space-y-1">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div><span className="text-gray-500">Total rows:</span> {log.rows_total}</div>
                              <div><span className="text-gray-500">Inserted:</span> {log.rows_inserted}</div>
                              <div><span className="text-gray-500">Updated:</span> {log.rows_updated}</div>
                              <div><span className="text-gray-500">Failed:</span> {log.rows_failed}</div>
                            </div>
                            {log.file_hash && (
                              <div className="text-gray-400 truncate">Hash: {log.file_hash}</div>
                            )}
                            {log.processed_by && (
                              <div className="text-gray-400">Processed by: {log.processed_by}</div>
                            )}
                            {log.error_details?.validation_errors?.length > 0 && (
                              <div className="mt-1">
                                <p className="font-semibold text-red-600">Errors:</p>
                                <div className="max-h-24 overflow-y-auto">
                                  {log.error_details.validation_errors.slice(0, 10).map((e, i) => (
                                    <div key={i} className="text-red-500">Row {e.row}: {e.errors?.join('; ')}</div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {log.error_details?.error && (
                              <div className="text-red-500">Error: {log.error_details.error}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pagination */}
                  {auditTotal > 10 && (
                    <div className="flex items-center justify-between pt-3">
                      <span className="text-xs text-gray-500">
                        Showing {auditPage * 10 + 1}-{Math.min((auditPage + 1) * 10, auditTotal)} of {auditTotal}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAuditPage(p => Math.max(0, p - 1))}
                          disabled={auditPage === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAuditPage(p => p + 1)}
                          disabled={(auditPage + 1) * 10 >= auditTotal}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* CSV Format Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSpreadsheet className="w-5 h-5 text-[#1B5C94]" />
                CSV Format Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-gray-800">Employee Master</h4>
                    <button
                      onClick={() => downloadTemplate('employee_master')}
                      className="text-xs text-[#1B5C94] hover:underline font-semibold flex items-center gap-1"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5" />
                      Download Template
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Required columns marked with *</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono space-y-0.5">
                    <div><span className="text-red-600">employee_code*</span>, <span className="text-red-600">name*</span>,</div>
                    <div><span className="text-red-600">employment_type*</span>, gender,</div>
                    <div>department, designation,</div>
                    <div>joining_date, status,</div>
                    <div>category, disability_flag,</div>
                    <div>is_primary_site, last_updated</div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    employment_type: permanent, contract, temporary, intern
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-gray-800">Work Hours</h4>
                    <button
                      onClick={() => downloadTemplate('work_hours')}
                      className="text-xs text-[#1B5C94] hover:underline font-semibold flex items-center gap-1"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5" />
                      Download Template
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Required columns marked with *</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono space-y-0.5">
                    <div><span className="text-red-600">employee_code*</span>,</div>
                    <div><span className="text-red-600">work_date*</span>, <span className="text-red-600">hours_worked*</span>,</div>
                    <div>overtime_hours, shift_type</div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Date formats: YYYY-MM-DD, DD/MM/YYYY, DD-Mon-YYYY
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HRMSDataImport;
